import React, { useState } from "react";
import { ReactComponent as VisaCardIcon } from "../../icons/visaCardIcon.svg";
import { ReactComponent as MasterCardIcon } from "../../icons/masterCardIcon.svg";
import { ReactComponent as AmexCardIcon } from "../../icons/amexCardIcon.svg";
import { ReactComponent as DiscoverCardIcon } from "../../icons/discoverCard.svg";
import { ReactComponent as CashappIcon } from "../../icons/cashappIcon.svg";
import { ReactComponent as AmazonPayIcon } from "../../icons/AmazonPayIcon.svg";
import { capitalizeString, formatDateString } from "../../utils/helpers";
import { useAddPaySetupIntent } from "../../hooks/paymenthooks";
import { DeleteConfirmationModal } from "../../molecules/Mobile/AppModal";
import { toast } from "sonner";

type PayMethodComponentProps = {
  paymentMethod: any;
  username: string;
  reloadPaymentMethods: () => void;
};

const PayMethodComponent: React.FC<PayMethodComponentProps> = ({
  username,
  paymentMethod,
  reloadPaymentMethods,
}) => {
  const { removePaymentMethod } = useAddPaySetupIntent(username);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const getCardIcon = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "visa":
        return <VisaCardIcon className="w-8 my-auto" />;
      case "mastercard":
        return <MasterCardIcon className="w-8 my-auto" />;
      case "amex":
        return <AmexCardIcon className="w-8 my-auto" />;
      case "discover":
        return <DiscoverCardIcon className="w-8 my-auto" />;
      case "cashapp":
        return <CashappIcon className="w-8 h-8 my-auto" />;
      case "amazon_pay":
        return <AmazonPayIcon className="w-8 h-6 my-auto" />;
      default:
        return <img src="/icons/unknown.svg" alt="Unknown" />;
    }
  };

  const handlePayRemoveMethod = async (paymentMethodId: string) => {
    const result = await removePaymentMethod(paymentMethodId);
    if (result) {
      reloadPaymentMethods();
      setIsOpenDeleteModal(false);
      toast.success("Payment method removed successfully", {
        position: "top-right",
        className: "bg-green-500 text-white",
        icon: "🎉",
      });
    } else {
      toast.error("Failed to remove payment method", {
        position: "top-right",
        className: "bg-red-500 text-white",
        icon: "🚫",
      });
    }
  };

  const handleEdit = () => {
    // TODO: Implement edit functionality
    console.log("Edit payment method:", paymentMethod);
  };

  return (
    <>
      <div className="flex flex-col bg-white border-[1px] border-[#E9E9E9] rounded-md mb-2 p-3 text-[14px] text-[#000000] font-normal font-raleway">
        <div className="flex flex-row items-center mb-3">
          {paymentMethod && paymentMethod.card ? (
            <>
              <div className="flex-shrink-0 border-[#E9E9E9] border-[1px] p-1 rounded-lg">
                {getCardIcon(paymentMethod.card.brand)}
              </div>
              <div className="flex-shrink-1 mr-5 flex flex-col justify-center min-w-0 ml-2 text-[16px] text-black">
                <div className="text-[#000000]">
                  ****{paymentMethod.card.last4 || ""}
                </div>
                
              </div>
              <div className="text-[16px] text-black">
                  Expires in{" "}
                  {formatDateString(
                    paymentMethod.card.exp_month,
                    paymentMethod.card.exp_year
                  )}
                </div>
            </>
          ) : (
            <>
              <div className="flex-shrink-0 border-[#E9E9E9] border-[1px] p-1 rounded-lg">
                {getCardIcon(paymentMethod.type)}
              </div>
              <div className="flex-1 flex flex-col justify-center min-w-0 ml-2">
                <div className="text-[#000000]">
                  {capitalizeString(paymentMethod.type)}
                </div>
                {paymentMethod &&
                paymentMethod?.cashapp &&
                paymentMethod?.cashapp?.cashtag ? (
                  <div className="text-[12px] text-[#808080]">
                    {paymentMethod?.cashapp?.cashtag || ""}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <button
            onClick={handleEdit}
            className="flex-1 bg-white border-[1px] border-[#E9E9E9] rounded-lg py-2 px-4 text-[14px] font-raleway text-primaryDark-0 hover:bg-slate-50 cursor-pointer"
          >
            Edit
          </button>
          <button
            onClick={() => setIsOpenDeleteModal(true)}
            className="flex-1 bg-white border-[1px] border-[#E9E9E9] rounded-lg py-2 px-4 text-[14px] font-raleway text-[#D42620] hover:bg-slate-50 cursor-pointer"
          >
            Delete
          </button>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={isOpenDeleteModal}
        closeDeleteModal={() => setIsOpenDeleteModal(false)}
        handleDelete={handlePayRemoveMethod}
        dataName="payment method"
        data={{
          ...paymentMethod,
          item_name: paymentMethod.card?.brand
            ? `${capitalizeString(paymentMethod.card.brand)} **** ${
                paymentMethod.card.last4
              }`
            : paymentMethod.type,
        }}
      />
    </>
  );
};

export default PayMethodComponent;
